import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import Icon from '@/common/components/Icon';
import { PayItem } from '@/apis/origin/finance/deposit';
import { useAppSelector } from '@/core/store/hooks';
import WalletChannelIcon from '@/sites/op7/components/WalletChannelIcon';

import PermanentModal from '../permanentModal';
import styles from './index.module.scss';

interface PayWayListProps {
  list?: PayItem[];
  payIdx: number;
  onChange: (val: number) => void;
  inModal?: boolean;
}

const PayWayList: React.FC<PayWayListProps> = ({
  list = [],
  payIdx = 0,
  onChange,
  inModal = false,
}) => {
  const screenBreakpoint = useAppSelector((state) => state.config.screenBreakpoint);
  const isMobile = useMemo(() => screenBreakpoint === 'md', [screenBreakpoint]);
  const [isExpand, setExpand] = useState(false);
  const [listHeight, setListHeight] = useState<number>();
  const listWrapRef = useRef<HTMLDivElement | null>(null);
  const showCount = isMobile || inModal ? 3 : 12;
  const virtualTutorialPayId = -1;

  useEffect(() => {
    setExpand(false);
  }, [list]);

  useEffect(() => {
    const listWrap = listWrapRef.current;
    if (!listWrap) return;

    const updateListHeight = () => {
      const firstItem = listWrap.querySelector<HTMLElement>(`.${styles.item}`);
      if (!firstItem) {
        setListHeight(undefined);
        return;
      }

      const itemHeight = firstItem.getBoundingClientRect().height;
      const computedStyle = window.getComputedStyle(listWrap);
      const rowGap = parseFloat(computedStyle.rowGap || computedStyle.gap || '0');
      const itemsPerRow = isMobile || inModal ? 3 : 6;
      const collapsedRows = isMobile || inModal ? 1 : 2;
      const totalRows = Math.ceil(list.length / itemsPerRow);
      const visibleRows = isExpand ? totalRows : Math.min(collapsedRows, totalRows);

      setListHeight(visibleRows * itemHeight + rowGap * Math.max(visibleRows - 1, 0) + 1);
    };

    updateListHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateListHeight();
    });

    resizeObserver.observe(listWrap);
    const firstItem = listWrap.querySelector<HTMLElement>(`.${styles.item}`);
    if (firstItem) {
      resizeObserver.observe(firstItem);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [list.length, isExpand, isMobile, inModal]);

  return (
    <div className={clsx(styles.payWayList, !inModal ? styles.autoPC : '')}>
      <div className={styles.title}>
        <span>充值方式</span>
        {/* <a className={styles.url}>网址：op7.io</a> */}

        <PermanentModal />
      </div>
      <div className={styles.list}>
        <div className={styles.listWrap} ref={listWrapRef} style={{ height: listHeight }}>
          {list.map((obj, index) => (
            <div
              className={clsx(styles.item, index === payIdx ? styles.active : '')}
              key={obj.payId}
              onClick={() => onChange(index)}
            >
              {/*  虚拟币教程 */}
              {obj.payId === virtualTutorialPayId ? (
                <WalletChannelIcon
                  type="virtual"
                  selected
                  size={16}
                  backgroundSize={20}
                  selectedBackgroundFill="var(--ThemeColor-Main)"
                  selectedIconColor="var(--ThemeColor-Main)"
                />
              ) : (
                <img src={obj.payImage} />
              )}
              <span className={styles.label}>{obj.payName}</span>
              {obj.cashMax > 0 ? (
                <span className={styles.amount}>
                  {obj.cashMin}-{obj.cashMax}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      {list.length > showCount && (
        <div className={styles.expendBox} onClick={() => setExpand(!isExpand)}>
          <span>{isExpand ? '收起' : '展开'}全部支付方式</span>
          <Icon
            className={clsx(styles.icon, isExpand ? styles.active : '')}
            src="/images/common/arrows_up.svg"
            size="10px"
            color="var(--ThemeColor-Main)"
          />
        </div>
      )}
    </div>
  );
};

export default PayWayList;
